import React, { FunctionComponent, useContext, useEffect } from 'react'
import { decode } from 'he'
import { useSelector, useDispatch } from 'react-redux'
import { pick, isEmpty, isNumber } from 'lodash'
import accounting from 'accounting'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { rebuild } from 'react-tooltip'

import { DateTimeDisplay, PageHeading } from '../../../components'
import Table from '../../../components/Table/next'
import ReportActions from '../../../components/ReportActions'
import BannerReferralsFilter from './BannerReferralsFilters'
import TotalsDisplay from './TotalsDisplay'

import { pruneObject } from '../../../utils/objectManagement'
import { getDefinition } from '../../../utils/dictionary'
import {
  exportBannerReferrals,
  getBannerReferrals,
} from '../../../actions/bannerReferrals'
import { BannerReferralsTableContext } from './index'
import { State } from '../../../reducers'

import { BannerReferralsStore } from '../../../typings/stores/BannerReferralsStore'
import { ReportStatus } from '../../../typings/report'

import styles from './table.module.css'

interface TableRow {
  referralId: string
  referralDate: string
  referralProductName: string
  referralDestination: string
  referralVisitorIp: string
  referralSubtracking: string
  clickId: string
  clickDate: string
  clickProduct: string
  clickAdvertiser: string
  saleId: string
  saleDate: string
  saleCommission: number
  saleIsCrossSale: boolean
}

interface TotalsDisplayFields {
  referrals: number
  clicks: number
  commissions: number
  sales: number
}

const BannerReferralsTable: FunctionComponent = () => {
  const dispatch = useDispatch()
  const bannerReferrals = useSelector<State, BannerReferralsStore>(
    state => state.bannerReferrals
  )

  const reportStatus = useSelector<State, ReportStatus>(state => ({
    ...pick(state.bannerReferrals, ['hasData', 'status', 'error']),
    loading: state.bannerReferrals.status === 'fetching',
    hasError: !isEmpty(state.bannerReferrals.error),
  }))
  const exportStatus = useSelector<State, ReportStatus>(state => ({
    ...pick(state.bannerReferrals.export, ['hasData', 'status', 'error']),
    loading: state.bannerReferrals.export.status === 'fetching',
    hasError: !isEmpty(state.bannerReferrals.export.error),
  }))

  const {
    changePage,
    changePageSize,
    changeSort,
    filters,
    pagination: { size, page },
    sort,
  } = useContext(BannerReferralsTableContext)

  const fetchReport = () =>
    dispatch(
      getBannerReferrals({ ...filters, ...pruneObject(sort), size, page })
    )
  const fetchExport = () =>
    dispatch(
      exportBannerReferrals({ ...filters, ...pruneObject(sort), size, page })
    )

  useEffect(() => {
    fetchReport()
  }, [size, page, sort])

  useEffect(() => {
    rebuild()
  })

  const total =
    bannerReferrals.data && bannerReferrals.data.pagination
      ? bannerReferrals.data.pagination.total
      : 0

  const tableData: TableRow[] = bannerReferrals.data.transactions.map<TableRow>(
    t => ({
      referralId: t.referral.clickId,
      referralDate: t.referral.clickDate,
      referralProductName: t.referral.cardTitle,
      referralDestination: t.referral.destinationName || '', // decode() needs a string
      referralVisitorIp: t.referral.visitorIp,
      referralSubtracking: t.referral.subtracking,
      clickId: t.click.id,
      clickDate: t.click.clickDate,
      clickProduct: t.click.cardTitle || '',
      clickAdvertiser: t.click.merchantName,
      saleId: t.sale.id,
      saleDate: t.sale.processDate,
      saleCommission: t.sale.commission,
      saleIsCrossSale: t.sale.isCrossSale,
    })
  )

  const totals = bannerReferrals.data.totals || null

  return (
    <>
      <PageHeading
        title="Referral Reports"
        description="This report contains all traffic, including clicks and commissions where applicable, generated through our referral programs from your website to a Red Ventures property."
      >
        <BannerReferralsFilter />
      </PageHeading>
      <ReportActions
        fetchExport={fetchExport}
        fetchReport={fetchReport}
        reportStatus={reportStatus}
        exportStatus={exportStatus}
      />
      {reportStatus.hasData && (
        <TotalsDisplay<TotalsDisplayFields>
          fields={[
            {
              text: 'Referrals',
              field: 'referrals',
            },
            {
              text: 'Clicks',
              field: 'clicks',
            },
            {
              text: 'Sales',
              field: 'sales',
            },
            {
              text: 'Commissions',
              field: 'commissions',
              render: totals => {
                return (
                  (!isEmpty(totals.commissions) ||
                    isNumber(totals.commissions)) &&
                  accounting.formatMoney(totals.commissions, '$')
                )
              },
            },
          ]}
          totals={totals}
        />
      )}
      <Table<TableRow[]>
        className={styles.table}
        data={tableData}
        onSortChange={changeSort}
        sort={sort}
        cols={[
          {
            text: 'Referral ID',
            field: 'referralId',
          },
          {
            text: 'Referral Date',
            sortable: true,
            field: 'referralDate',
            render: row =>
              row.referralDate && (
                <DateTimeDisplay
                  datetime={DateTime.fromSQL(row.referralDate)}
                />
              ),
          },
          {
            text: 'Product Name',
            sortable: true,
            field: 'referralProductName',
            render: row => decode(row.referralProductName),
          },
          {
            text: 'Destination',
            field: 'referralDestination',
          },
          {
            text: 'Visitor IP',
            field: 'referralVisitorIp',
          },
          {
            text: 'Subtracking',
            sortable: true,
            field: 'referralSubtracking',
          },
          {
            text: 'Click ID',
            field: 'clickId',
          },
          {
            text: 'Click Date',
            sortable: true,
            field: 'clickDate',
            render: row =>
              row.clickDate && (
                <DateTimeDisplay datetime={DateTime.fromSQL(row.clickDate)} />
              ),
          },
          {
            text: 'Clicked Product',
            sortable: true,
            field: 'clickProduct',
            render: row => decode(row.clickProduct),
          },
          {
            text: 'Clicked Advertiser',
            field: 'clickAdvertiser',
          },
          {
            text: 'Sale ID',
            field: 'saleId',
          },
          {
            text: 'Sale Date',
            sortable: true,
            field: 'saleDate',
            render: row =>
              row.saleDate && (
                <DateTimeDisplay datetime={DateTime.fromSQL(row.saleDate)} />
              ),
          },
          {
            text: 'Commission',
            sortable: true,
            field: 'saleCommission',
            render: row =>
              row.saleCommission &&
              accounting.formatMoney(row.saleCommission, '$'),
          },
          {
            text: 'Cross Sale',
            field: 'saleIsCrossSale',
            className: styles.crossSale,
            render: row =>
              row.saleIsCrossSale && (
                <div
                  data-tip={getDefinition('cross sale')}
                  data-for="global-tooltip"
                >
                  <FontAwesomeIcon
                    icon={['fas', 'expand-arrows-alt']}
                    className={styles.icon}
                  />
                </div>
              ),
          },
        ]}
        isLoading={reportStatus.loading}
        pagination={{
          currentPage: page,
          onPageSizeUpdate: changePageSize,
          onPaginate: changePage,
          pageSize: size,
          total: total,
        }}
      />
    </>
  )
}

export default BannerReferralsTable
